import type { Component } from 'vue'
import {
  SafetyCertificateOutlined,
  FolderOutlined,
  AppstoreOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  UserOutlined,
  FileSearchOutlined,
  StarOutlined,
  BankOutlined,
  TeamOutlined,
  HomeOutlined,
  GlobalOutlined,
  RobotOutlined,
  PhoneOutlined,
  NotificationOutlined,
  IdcardOutlined,
  SettingOutlined,
  FileTextOutlined,
  PartitionOutlined,
  ApartmentOutlined,
  ControlOutlined,
  QuestionCircleOutlined,
  ReadOutlined,
  AlertOutlined,
  CommentOutlined,
  CloudDownloadOutlined,
  StopOutlined,
} from '@ant-design/icons-vue'

/** 图标名称到组件的映射（键为 PascalCase，与 Snowy 存库的 kebab 名通过下方转换函数对应） */
export const iconMap: Record<string, Component> = {
  SafetyCertificateOutlined,
  FolderOutlined,
  AppstoreOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  UserOutlined,
  FileSearchOutlined,
  StarOutlined,
  BankOutlined,
  TeamOutlined,
  HomeOutlined,
  GlobalOutlined,
  RobotOutlined,
  NotificationOutlined,
  PhoneOutlined,
  IdcardOutlined,
  SettingOutlined,
  FileTextOutlined,
  PartitionOutlined,
  ApartmentOutlined,
  ControlOutlined,
  QuestionCircleOutlined,
  ReadOutlined,
  CommentOutlined,
  AlertOutlined,
  CloudDownloadOutlined,
  StopOutlined,
}

/** Ant Design Vue 图标 PascalCase → 存库常用 kebab（如 ControlOutlined → control-outlined） */
export function menuIconPascalToKebab(pascal: string): string {
  return pascal
    .replace(/([A-Z])/g, '-$1')
    .replace(/^-/, '')
    .toLowerCase()
}

/** kebab 图标名 → PascalCase（用于动态匹配 @ant-design/icons-vue） */
export function menuIconKebabToPascal(kebab: string): string {
  return kebab
    .split('-')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join('')
}

/** 菜单表单可选图标（来自 iconMap，名称有序） */
export const menuIconPickerEntries = Object.entries(iconMap)
  .map(([pascal, component]) => ({ pascal, component }))
  .sort((a, b) => a.pascal.localeCompare(b.pascal))

/**
 * 根据接口/库表中的 icon 字符串解析为图标组件（支持 PascalCase 与 kebab-case）
 */
export function resolveMenuIconComponent(icon?: string | null): Component | null {
  if (!icon?.trim()) return null
  const raw = icon.trim()
  if (iconMap[raw]) return iconMap[raw]
  const pascalFromKebab = menuIconKebabToPascal(raw)
  if (iconMap[pascalFromKebab]) return iconMap[pascalFromKebab]
  // 未收录的图标回退为 Folder，避免 import * as AntIcons 把全量图标打进主包
  return FolderOutlined
}

/** 渲染用菜单项（含图标组件） */
export interface MenuItem {
  key: string
  label: string
  icon: Component
  path?: string
  children?: { key: string; label: string; path: string; icon?: Component }[]
}

/** 子菜单默认图标 */
export const childIconMap: Record<string, Component> = {
  case: FileSearchOutlined,
  'help-report': AlertOutlined,
  evaluation: StarOutlined,
  enterprise: BankOutlined,
  agency: TeamOutlined,
  repatriation: HomeOutlined,
  country: GlobalOutlined,
  message: NotificationOutlined,
  'ai-assistant': RobotOutlined,
  'voice-call': PhoneOutlined,
  'user-message': CommentOutlined,
  overseasUserMessage: CommentOutlined,
  'permission-management': FileSearchOutlined,
  'user-management': UserOutlined,
  'user-cancel-apply': StopOutlined,
  'user-agreement': FileSearchOutlined,
  'security-log': FileSearchOutlined,
  'risk-alert': WarningOutlined,
  'warning-release': NotificationOutlined,
  'weekly-report': NotificationOutlined,
  personnel: IdcardOutlined,
  departure: GlobalOutlined,
  'system-config': SettingOutlined,
  'information-release': FileTextOutlined,
  region: PartitionOutlined,
  'department-management': ApartmentOutlined,
  'quiz-question': QuestionCircleOutlined,
  'social-post': ReadOutlined,
  'social-comment': CommentOutlined,
  'situation-report': NotificationOutlined,
  'app-version': CloudDownloadOutlined,
  'dashboard-screen-region': GlobalOutlined,
  'dashboard-screen-org': ApartmentOutlined,
  /** 与 sys_resource.NAME=appversion 对齐 */
  appversion: FileTextOutlined,
}

/**
 * 子菜单 key → 前端路由 path。
 * 用于覆盖接口仍返回旧 path（如案件管理指向 `/`）的情况，避免点击进首页、面包屑与选中态错乱。
 */
export const menuChildPathOverrides: Record<string, string> = {
  case: '/emergency/case/list',
  caseTodo: '/overseas/caseTodo',
  待办任务: '/overseas/caseTodo',
  caseDone: '/overseas/caseDone',
  已办任务: '/overseas/caseDone',
  'help-report': '/overseas/helpReport',
  'situation-report': '/emergency/situation-report/list',
  evaluation: '/emergency/evaluation/list',
  enterprise: '/info/enterprise/list',
  agency: '/info/agency/list',
  repatriation: '/info/repatriation/list',
  personnel: '/info/personnel/list',
  departure: '/info/departure/list',
  country: '/geo/country/list',
  region: '/geo/region/list',
  'ai-assistant': '/service/ai-assistant/list',
  'voice-call': '/service/voice-call/list',
  'user-message': '/service/user-message/list',
  overseasUserMessage: '/service/user-message/list',
  用户留言: '/service/user-message/list',
  'permission-management': '/system/permission/list',
  'user-management': '/system/user/list',
  'user-cancel-apply': '/system/user-cancel/list',
  注销申请: '/system/user-cancel/list',
  'department-management': '/system/department/list',
  'user-agreement': '/other/user-agreement/list',
  message: '/other/message/list',
  站内信息: '/other/message/list',
  站内信: '/other/message/list',
  消息管理: '/other/message/list',
  'security-log': '/system/security-log/list',
  'system-config': '/system/config/list',
  'risk-alert': '/warning/risk-alert/list',
  'warning-release': '/warning/release/list',
  'weekly-report': '/warning/weekly-report/list',
  'information-release': '/warning/information-release/list',
  'quiz-question': '/other/quiz-question/list',
  'social-post': '/other/social-post/list',
  'social-comment': '/other/social-comment/list',
  'app-version': '/other/app-version/list',
  'dashboard-screen-region': '/dashboard-screen/region',
  'dashboard-screen-org': '/dashboard-screen/org',
  大屏地区: '/dashboard-screen/region',
  大屏组织: '/dashboard-screen/org',
  banner: '/other/banner/list',
  feedback: '/other/feedback/list',
}

const USER_MESSAGE_PATH = '/service/user-message/list'
const SITE_MESSAGE_PATH = '/other/message/list'
const USER_MESSAGE_TITLES = new Set(['用户留言'])
const SITE_MESSAGE_TITLES = new Set(['站内信息', '站内信', '消息管理'])

/**
 * 解析子菜单实际路由 path。
 * 用户留言 / 站内信息必须按标题拆开：Snowy 里两条都可能 NAME=message、PATH=/other/message/list，
 * 若先按 name 覆盖，点「用户留言」会进站内信，点新 path 又会 404。
 */
export function resolveMenuChildPath(
  key: string,
  path?: string,
  name?: string,
  title?: string,
): string {
  const titleText = (title ?? '').trim()
  const nameText = (name ?? '').trim()
  if (USER_MESSAGE_TITLES.has(titleText) || USER_MESSAGE_TITLES.has(nameText)) {
    return USER_MESSAGE_PATH
  }
  if (SITE_MESSAGE_TITLES.has(titleText) || SITE_MESSAGE_TITLES.has(nameText)) {
    return SITE_MESSAGE_PATH
  }
  const override =
    menuChildPathOverrides[key] ??
    (nameText ? menuChildPathOverrides[nameText] : undefined) ??
    (titleText ? menuChildPathOverrides[titleText] : undefined)
  if (override) return override
  const v = (path ?? '').trim()
  if (!v) return ''
  return v.startsWith('/') ? v : `/${v}`
}

/** 默认菜单（接口失败或返回空时使用） */
export const defaultMenus: MenuItem[] = [
  {
    key: 'emergency',
    label: '一键求助与应急指挥',
    icon: SafetyCertificateOutlined,
    children: [
      { key: 'case', label: '案件管理', path: '/emergency/case/list', icon: childIconMap.case },
      {
        key: 'help-report',
        label: '报案记录',
        path: '/overseas/helpReport',
        icon: childIconMap['help-report'],
      },
      {
        key: 'situation-report',
        label: '情况反映记录',
        path: '/emergency/situation-report/list',
        icon: childIconMap['situation-report'],
      },
      {
        key: 'evaluation',
        label: '评价管理',
        path: '/emergency/evaluation/list',
        icon: childIconMap.evaluation,
      },
    ],
  },
  {
    key: 'info',
    label: '信息管理',
    icon: FolderOutlined,
    children: [
      {
        key: 'personnel',
        label: '人员管理',
        path: '/info/personnel/list',
        icon: childIconMap.personnel,
      },
      {
        key: 'departure',
        label: '出国管理',
        path: '/info/departure/list',
        icon: childIconMap.departure,
      },
      {
        key: 'enterprise',
        label: '企业管理',
        path: '/info/enterprise/list',
        icon: childIconMap.enterprise,
      },
      {
        key: 'repatriation',
        label: '归国管理',
        path: '/info/repatriation/list',
        icon: childIconMap.repatriation,
      },
      { key: 'agency', label: '驻外机构', path: '/info/agency/list', icon: childIconMap.agency },
    ],
  },
  {
    key: 'warning',
    label: '安全预警与信息发布',
    icon: AppstoreOutlined,
    children: [
      {
        key: 'risk-alert',
        label: '风险预警',
        path: '/warning/risk-alert/list',
        icon: childIconMap['risk-alert'],
      },
      {
        key: 'warning-release',
        label: '预警发布',
        path: '/warning/release/list',
        icon: childIconMap['warning-release'],
      },
      {
        key: 'weekly-report',
        label: '安全周报',
        path: '/warning/weekly-report/list',
        icon: childIconMap['weekly-report'],
      },
      {
        key: 'information-release',
        label: '信息发布',
        path: '/warning/information-release/list',
        icon: childIconMap['information-release'],
      },
    ],
  },
  {
    key: 'geo',
    label: '地理信息管理',
    icon: EnvironmentOutlined,
    children: [
      { key: 'country', label: '国别管理', path: '/geo/country/list', icon: childIconMap.country },
      { key: 'region', label: '地区管理', path: '/geo/region/list', icon: childIconMap.region },
    ],
  },
  {
    key: 'service',
    label: '智能服务管理',
    icon: ClockCircleOutlined,
    children: [
      {
        key: 'ai-assistant',
        label: 'AI助手',
        path: '/service/ai-assistant/list',
        icon: childIconMap['ai-assistant'],
      },
      {
        key: 'voice-call',
        label: '语音通话',
        path: '/service/voice-call/list',
        icon: childIconMap['voice-call'],
      },
      {
        key: 'user-message',
        label: '用户留言',
        path: '/service/user-message/list',
        icon: childIconMap['user-message'],
      },
    ],
  },
  {
    key: 'other',
    label: '其他管理',
    icon: WarningOutlined,
    path: '/other',
    children: [
      {
        key: 'message',
        label: '站内信息',
        path: '/other/message/list',
        icon: childIconMap.message,
      },
      {
        key: 'user-agreement',
        label: '用户协议管理',
        path: '/other/user-agreement/list',
        icon: childIconMap['user-agreement'],
      },
      {
        key: 'quiz-question',
        label: '问答题目管理',
        path: '/other/quiz-question/list',
        icon: childIconMap['quiz-question'],
      },
      {
        key: 'social-post',
        label: '帖子管理',
        path: '/other/social-post/list',
        icon: childIconMap['social-post'],
      },
      {
        key: 'social-comment',
        label: '评论管理',
        path: '/other/social-comment/list',
        icon: childIconMap['social-comment'],
      },
      {
        key: 'app-version',
        label: '版本管理',
        path: '/other/app-version/list',
        icon: childIconMap['app-version'],
      },
    ],
  },
  {
    key: 'system',
    label: '系统管理',
    icon: UserOutlined,
    path: '/system',
    children: [
      {
        key: 'department-management',
        label: '部门管理',
        path: '/system/department/list',
        icon: childIconMap['department-management'],
      },
      {
        key: 'user-management',
        label: '用户管理',
        path: '/system/user/list',
        icon: childIconMap['user-management'],
      },
      {
        key: 'user-cancel-apply',
        label: '注销申请',
        path: '/system/user-cancel/list',
        icon: childIconMap['user-cancel-apply'],
      },
      {
        key: 'permission-management',
        label: '权限管理',
        path: '/system/permission/list',
        icon: childIconMap['permission-management'],
      },
      {
        key: 'security-log',
        label: '安全日志',
        path: '/system/security-log/list',
        icon: childIconMap['security-log'],
      },
      {
        key: 'system-config',
        label: '系统配置',
        path: '/system/config/list',
        icon: childIconMap['system-config'],
      },
    ],
  },
]
